import { useCallback, useEffect, useState } from 'react';
import { api, TRANSFER_CENTER_SITE, type BoardShift, type OpenShift } from '../lib/api';
import { addDays, dayLabel, isoToday } from '../lib/dates';
import { navigate } from '../lib/router';

const SLOTS: [string, string][] = [
  ['night-am', 'Night-AM · 2am–10am'],
  ['daytime', 'Daytime · 10am–6pm'],
  ['swing', 'Swing · 6pm–2am'],
];

const when = (s: { start: string | null; end: string | null; crossesMidnight: boolean }) =>
  s.start && s.end ? `${s.start}–${s.end}${s.crossesMidnight ? ' (next day)' : ''}` : 'time not set';

function byDate<T extends { date: string }>(rows: T[]): [string, T[]][] {
  const out = new Map<string, T[]>();
  for (const r of rows) out.set(r.date, [...(out.get(r.date) ?? []), r]);
  return [...out.entries()].sort(([a], [b]) => a.localeCompare(b));
}

/**
 * The Deaconess Transfer Center board (D108).
 *
 * Every other hospital's schedule is drafted and handed to a physician. This one cannot be:
 * the Transfer Center's three shifts follow no pattern at all, so the office posts the open
 * dates and physicians claim them. John asked for exactly this on Sep 29.
 *
 * Only physicians credentialed at the Transfer Center see the tab, because a board of shifts
 * nobody else may work is noise on everybody else's screen — and, worse, an invitation that
 * gets refused at the last step.
 *
 * A date they cannot take is shown with the reason rather than quietly dropped. "Why can't I
 * see Thursday?" is the question this screen exists to stop the office having to answer, and
 * the reason is nearly always something the physician can act on themselves.
 */
export function TransferCenter({ abbreviation }: { abbreviation: string }) {
  const [rows, setRows] = useState<OpenShift[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .transferCenterOpen(abbreviation)
      .then(setRows)
      .catch((e: Error) => setProblem(e.message));
  }, [abbreviation]);

  useEffect(load, [load]);

  const act = async (id: string, go: () => Promise<unknown>) => {
    setBusy(id);
    setProblem(null);
    try {
      await go();
      load();
    } catch (e) {
      setProblem((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const mine = (rows ?? []).filter((r) => r.mine);
  const open = (rows ?? []).filter((r) => !r.mine);

  return (
    <section className="logistics">
      <h1>Transfer Center</h1>
      <p className="muted">
        The office posts open dates here and you put your name against the ones you want. These are Deaconess Transfer Center
        shifts only — they do not replace your requests for a hospital week.
      </p>

      {problem && <p className="error">{problem}</p>}
      {!rows && !problem && <p className="muted">Loading the board…</p>}

      {rows && (
        <>
          <h2>Open dates</h2>
          {open.length === 0 && <p className="muted">Nothing posted at the moment. The office puts new dates up here as they come.</p>}
          {byDate(open).map(([date, shifts]) => (
            <div key={date} className="card">
              <h3>{dayLabel(date)}</h3>
              <ul className="plain">
                {shifts.map((s) => (
                  <li key={s.id}>
                    {s.label} <span className="muted">{when(s)}</span>{' '}
                    {s.takeable ? (
                      <button type="button" disabled={busy === s.id} onClick={() => act(s.id, () => api.claimTransferShift(abbreviation, s.id))}>
                        Take it
                      </button>
                    ) : (
                      <span className="small muted">— {s.blockedBy}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </>
      )}
      {rows && mine.length > 0 && (
        <>
          <h2>Yours ({mine.length})</h2>
          <ul className="plain">
            {mine.map((s) => (
              <li key={s.id}>
                <strong>{dayLabel(s.date)}</strong> · {s.label} {when(s)}{' '}
                <button type="button" className="linklike" disabled={busy === s.id} onClick={() => act(s.id, () => api.releaseTransferShift(abbreviation, s.id))}>
                  give it back
                </button>
              </li>
            ))}
          </ul>
          <p className="small muted">A shift can be given back until the day before it runs. After that, ring the office.</p>
        </>
      )}

    </section>
  );
}

/**
 * The office's side: what is posted, who has taken it, and how to post more.
 *
 * Posting is a date range and a set of shifts rather than one date at a time, because the
 * office posts a month of Swings in one go and clicking thirty times is how a screen stops
 * being used. Posting the same range twice is safe — a date already up is left exactly as it
 * is, claimed or not.
 */
export function TransferCenterBoard() {
  const [rows, setRows] = useState<BoardShift[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [from, setFrom] = useState(isoToday());
  const [to, setTo] = useState(addDays(isoToday(), 13));
  const [slots, setSlots] = useState<string[]>(['night-am', 'daytime', 'swing']);

  const load = useCallback(() => {
    api
      .transferCenterBoard(TRANSFER_CENTER_SITE)
      .then(setRows)
      .catch((e: Error) => setProblem(e.message));
  }, []);

  useEffect(load, [load]);

  const dates = (): string[] => {
    const out: string[] = [];
    for (let d = from; d <= to && out.length < 120; d = addDays(d, 1)) out.push(d);
    return out;
  };

  const post = async () => {
    setBusy(true);
    setProblem(null);
    setNote(null);
    try {
      const r = await api.postTransferDates(TRANSFER_CENTER_SITE, dates(), slots);
      setNote(`Posted ${r.posted} shift${r.posted === 1 ? '' : 's'}${r.alreadyThere ? `, ${r.alreadyThere} were already up` : ''}.`);
      load();
    } catch (e) {
      setProblem((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    setBusy(true);
    setProblem(null);
    try {
      await api.unpostTransferShift(TRANSFER_CENTER_SITE, id);
      load();
    } catch (e) {
      setProblem((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const count = dates().length * slots.length;

  return (
    <section className="logistics">
      <div className="gridtools noprint">
        <h1>Transfer Center sign-up</h1>
        <button type="button" className="linklike" onClick={() => navigate('/admin/logistics')}>‹ Back to the week</button>
      </div>
      <p className="muted">
        The Transfer Center has no pattern to draft from, so its shifts are posted here and physicians credentialed at
        Deaconess claim them themselves.
      </p>

      {problem && <p className="error">{problem}</p>}
      {note && <p className="small">{note}</p>}

      <div className="card noprint">
        <h2>Post open dates</h2>
        <div className="tcdates">
          <label>
            From <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label>
            To <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
        </div>
        <fieldset className="tcshifts">
          <legend className="small">Which shifts</legend>
          {SLOTS.map(([id, label]) => (
            <label key={id} className="check">
              <input
                type="checkbox"
                checked={slots.includes(id)}
                onChange={(e) => setSlots((was) => (e.target.checked ? [...was, id] : was.filter((s) => s !== id)))}
              />{' '}
              {label}
            </label>
          ))}
        </fieldset>
        <button type="button" disabled={busy || slots.length === 0 || to < from} onClick={post}>
          Post {count} shift{count === 1 ? '' : 's'}
        </button>
        <p className="small muted">Posting a date that is already up changes nothing — it will not unseat anybody who has signed up.</p>
      </div>

      {!rows && !problem && <p className="muted">Loading the board…</p>}
      {rows && rows.length === 0 && <p className="muted">Nothing posted from today forward.</p>}
      {rows &&
        byDate(rows).map(([date, shifts]) => (
          <div key={date} className="card">
            <h3>{dayLabel(date)}</h3>
            <table className="plain tcboard">
              <colgroup>
                <col className="tc-shift" />
                <col className="tc-when" />
                <col className="tc-who" />
                <col className="tc-act" />
              </colgroup>
              <tbody>
                {shifts.map((s) => (
                  <tr key={s.id}>
                    <td>{s.label}</td>
                    <td className="muted">{when(s)}</td>
                    <td>{s.takenBy ?? <span className="muted">open</span>}</td>
                    <td>
                      {!s.takenBy && (
                        <button type="button" className="linklike small" disabled={busy} onClick={() => remove(s.id)}>
                          take it down
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
    </section>
  );
}
